import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, FileText, Users, CreditCard } from 'lucide-react';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="legal-page">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <h1 className="font-heading font-bold text-4xl uppercase text-white mb-8">Legal</h1>
        
        {/* Terms of Service */}
        <section className="stat-card p-8 mb-8" id="terms">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-blue-400" />
            <h2 className="font-heading font-bold text-2xl text-white">Terms of Service</h2>
          </div>
          
          <div className="space-y-6 text-zinc-300 text-sm leading-relaxed">
            <p className="text-zinc-400 italic">Last Updated: February 2026</p>
            
            <div>
              <h3 className="font-semibold text-white mb-2">1. Acceptance of Terms</h3>
              <p>By accessing or using Slab Society ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">2. Age Requirement</h3>
              <p className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
                <strong className="text-amber-400">You must be at least 18 years of age to use Slab Society.</strong> By creating an account, you confirm you are 18 or older. Users under 18 are prohibited from trading, buying, or selling on this platform.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">3. Commission Structure</h3>
              <p className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-lg">
                <strong className="text-emerald-400">Slab Society charges 0% commission on all sales.</strong> Sellers keep 100% of their sale price. The only fees buyers pay are:
              </p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>Shipping costs (set by seller via Shippo)</li>
                <li>3.5% payment processing fee (charged by our payment processor)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">4. Seller Membership</h3>
              <p>To list items for sale, users must subscribe to our Seller Membership at $20/month. This membership grants unlimited listings with zero commission fees.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">5. Prohibited Items</h3>
              <p>The following items are prohibited from being listed:</p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>Counterfeit or fake trading cards</li>
                <li>Stolen merchandise</li>
                <li>Items that violate intellectual property rights</li>
                <li>Any items illegal under applicable law</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">6. Disputes</h3>
              <p>Slab Society facilitates transactions between buyers and sellers but is not a party to those transactions. Disputes should be resolved directly between parties. We may assist in mediation but are not liable for transaction outcomes.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">7. Account Termination</h3>
              <p>We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or harm the community.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">8. Limitation of Liability</h3>
              <p>Slab Society is provided "as is" without warranties. We are not liable for any damages arising from use of the platform, including but not limited to lost profits, data loss, or transaction disputes.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">9. Modifications</h3>
              <p>We may update these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the new terms.</p>
            </div>
          </div>
        </section>
        
        {/* Privacy Policy */}
        <section className="stat-card p-8" id="privacy">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-emerald-400" />
            <h2 className="font-heading font-bold text-2xl text-white">Privacy Policy</h2>
          </div>
          
          <div className="space-y-6 text-zinc-300 text-sm leading-relaxed">
            <p className="text-zinc-400 italic">Last Updated: February 2026</p>
            
            <div>
              <h3 className="font-semibold text-white mb-2">1. Information We Collect</h3>
              <p>We collect information you provide directly:</p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>Account information (name, email, password)</li>
                <li>Payment information (processed securely via Stripe)</li>
                <li>Shipping addresses for orders</li>
                <li>Card listings and images you upload</li>
                <li>Messages between buyers and sellers</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">2. How We Use Your Information</h3>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>To provide and improve our services</li>
                <li>To process transactions and send related information</li>
                <li>To send promotional communications (you can opt out)</li>
                <li>To detect and prevent fraud</li>
                <li>To comply with legal obligations</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">3. Information Sharing</h3>
              <p>We do not sell your personal information. We share data only with:</p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>Payment processors (Stripe) for transactions</li>
                <li>Shipping providers (Shippo) for delivery</li>
                <li>Law enforcement when legally required</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">4. Data Security</h3>
              <p>We use industry-standard security measures including encryption, secure servers, and regular security audits. However, no method of transmission over the Internet is 100% secure.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">5. Your Rights</h3>
              <p>You have the right to:</p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">6. Cookies</h3>
              <p>We use essential cookies to keep you logged in and improve your experience. We do not use third-party advertising cookies.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">7. Contact Us</h3>
              <p>For privacy-related questions, contact us at privacy@slabsociety.com</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
